import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Gem, ArrowLeft, Layers, Film, Play, Share2, Clapperboard, ArrowRight,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { getWisdomMoments } from '../services/videoActivationAPI';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const FONT_A = "'Caveat', cursive";

const CAT_THEME = {
  life_advice:         { label: 'Life Advice',         bg: '#EDF2E1', fg: '#48611F', dot: '#6B8E23' },
  family_history:      { label: 'Family History',      bg: '#F1EBE0', fg: '#7A5C2E', dot: '#8B7355' },
  cultural_tradition:  { label: 'Cultural Tradition',  bg: '#FBEFD8', fg: '#9A6A0A', dot: '#C08A2E' },
  personal_story:      { label: 'Personal Story',      bg: '#E7EFF8', fg: '#2E5C8A', dot: '#3B72C4' },
  blessing:            { label: 'Blessing',            bg: '#F0EBF4', fg: '#6B4B7E', dot: '#7B6B8E' },
  humor:               { label: 'Humor',               bg: '#F9EAF0', fg: '#9A3A5B', dot: '#B0506B' },
  other:               { label: 'Other',               bg: '#F0EFE7', fg: '#5A6150', dot: '#828A76' },
};

const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
const avatarColor = (name) => AVATAR_COLORS[name ? name.charCodeAt(0) % AVATAR_COLORS.length : 0];

function fmtTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

export default function WisdomSpeakerProfile() {
  const { speakerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!speakerId) return;
    getWisdomMoments({ speaker_id: speakerId })
      .then(setData)
      .catch(() => setData({ moments: [], stats: {}, speakers: [] }))
      .finally(() => setLoading(false));
  }, [speakerId]);

  const moments = data?.moments || [];
  const speaker = moments[0]?.speaker || data?.speakers?.find(s => s.id === speakerId) || { name: 'Speaker', initial: '?' };

  // Group by category
  const groups = {};
  moments.forEach(m => {
    if (!groups[m.category]) groups[m.category] = [];
    groups[m.category].push(m);
  });

  // Count unique stories
  const storyIds = new Set(moments.filter(m => m.video?.story_id).map(m => m.video.story_id));

  // Featured quote (first moment)
  const featured = moments[0];

  if (loading) {
    return (
      <HubLayout activeView="wisdom">
        <div style={{ maxWidth: 940, margin: '0 auto', padding: '34px 32px', textAlign: 'center', color: '#828A76' }}>Loading...</div>
      </HubLayout>
    );
  }

  return (
    <HubLayout activeView="wisdom">
      {/* Hero band */}
      <div style={{
        background: 'linear-gradient(160deg, #3E4F22, #556B2F 60%, #63792F)',
        color: '#fff', padding: '26px 40px 34px',
      }}>
        <div style={{ maxWidth: 940, margin: '0 auto' }}>
          <button onClick={() => navigate('/wisdom')} style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
            color: 'rgba(255,255,255,.7)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: FONT_B, padding: 0, marginBottom: 20,
          }}>
            <ArrowLeft size={16} /> All Family Wisdom
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{
              width: 96, height: 96, borderRadius: '50%', background: avatarColor(speaker.name),
              color: '#fff', fontWeight: 700, fontSize: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, border: '4px solid rgba(255,255,255,.3)',
            }}>{speaker.initial}</div>
            <div>
              <div style={{ fontFamily: FONT_A, fontSize: 20, color: '#D9E4B8', marginBottom: 2 }}>Words of wisdom from</div>
              <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 36, margin: 0, lineHeight: 1.1 }}>{speaker.name}</h1>
              <div style={{ display: 'flex', gap: 24, marginTop: 12, fontSize: 15, opacity: .85 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Gem size={16} /> {moments.length} moment{moments.length !== 1 ? 's' : ''}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Layers size={16} /> {Object.keys(groups).length} theme{Object.keys(groups).length !== 1 ? 's' : ''}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Film size={16} /> {storyIds.size} stor{storyIds.size !== 1 ? 'ies' : 'y'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 940, margin: '0 auto', padding: '32px 32px 90px' }}>
        {/* Share card */}
        {featured && (
          <div style={{
            background: 'linear-gradient(160deg, #FBF7EC, #F3F1DF)', border: '1px solid #E6DFC6',
            borderRadius: 20, padding: '38px 44px', marginBottom: 36, position: 'relative', overflow: 'hidden',
          }}>
            <span style={{ position: 'absolute', top: -20, right: 20, fontFamily: FONT_H, fontSize: 180, color: 'rgba(217,199,154,.2)', lineHeight: 1 }}>"</span>
            <div style={{ fontFamily: FONT_A, fontSize: 18, color: '#B5760A', marginBottom: 8 }}>Keepsake</div>
            <p style={{ fontFamily: FONT_H, fontWeight: 500, fontStyle: 'italic', fontSize: 30, color: '#2C2F26', margin: '0 0 18px', lineHeight: 1.35, maxWidth: 560, position: 'relative' }}>
              "{featured.original_text}"
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarColor(speaker.name), color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{speaker.initial}</div>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26', fontFamily: FONT_B }}>{speaker.name}</span>
            </div>
          </div>
        )}

        {/* Category groups */}
        {Object.entries(groups).map(([catKey, catMoments]) => {
          const ct = CAT_THEME[catKey] || CAT_THEME.other;
          return (
            <div key={catKey} style={{ marginBottom: 36 }}>
              {/* Group header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ width: 11, height: 11, borderRadius: '50%', background: ct.dot, flexShrink: 0 }} />
                <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, color: '#2C2F26', margin: 0 }}>{ct.label}</h3>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#556B2F', background: '#F0F5E8', padding: '2px 10px', borderRadius: 999, fontFamily: FONT_B }}>{catMoments.length}</span>
                <div style={{ flex: 1, height: 1, background: '#E4E1D2', marginLeft: 8 }} />
              </div>

              {/* Cards (no speaker row since this is a single-speaker page) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="wsp-grid">
                {catMoments.map(m => (
                  <div key={m.id} style={{
                    background: '#fff', border: '1px solid #E4E1D2', borderRadius: 18, padding: '22px 24px 18px',
                    boxShadow: '0 6px 22px rgba(40,46,28,.07)', cursor: 'pointer',
                    transition: 'transform .15s, box-shadow .15s',
                  }}
                    onClick={() => navigate(`/stories/${m.video?.story_id || ''}`)}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <span style={{ fontFamily: FONT_H, fontSize: 40, lineHeight: '28px', color: '#D9C79A' }}>"</span>
                    <p style={{
                      fontFamily: FONT_H, fontWeight: 500, fontStyle: 'italic', fontSize: 18, lineHeight: 1.5,
                      color: '#33372B', margin: '0 0 16px',
                      overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                    }}>{m.original_text}</p>

                    <div style={{ borderTop: '1px solid #F0EEE3', paddingTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#9AA088', fontFamily: FONT_B }}>
                      {m.video?.story_title && <>
                        <Play size={12} /> <span style={{ color: '#556B2F', fontWeight: 600 }}>{m.video.story_title}</span> · {fmtTime(m.start_ms)}
                      </>}
                      {m.created_at && <span style={{ marginLeft: 'auto' }}>{new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {moments.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#828A76' }}>
            <Gem size={32} color="#D9C79A" style={{ marginBottom: 12 }} />
            <p style={{ fontSize: 18, fontFamily: FONT_H }}>No wisdom moments yet for this speaker.</p>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 700px) { .wsp-grid { grid-template-columns: 1fr !important; } }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
      `}</style>
    </HubLayout>
  );
}
